import React, { useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Clock, Check, ArrowRight } from "lucide-react";
import Reveal from "./Reveal";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function Booking() {
  const [form, setForm] = useState({
    name: "",
    email: "",
    company: "",
    preferred: "",
    notes: "",
  });
  const [loading, setLoading] = useState(false);
  const [done, setDone] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    if (!form.name || !form.email) return toast.error("Name and email are required.");
    setLoading(true);
    const messageParts = [
      "Consultation request via website.",
      form.preferred ? `Preferred time: ${form.preferred}` : null,
      form.notes ? `Notes: ${form.notes}` : null,
    ].filter(Boolean);
    try {
      await axios.post(`${API}/contact`, {
        name: form.name,
        email: form.email,
        company: form.company,
        message: messageParts.join("\n"),
      });
      setDone(true);
      toast.success("Request received — we'll be in touch shortly.");
    } catch (err) {
      const msg = err?.response?.data?.detail || "Something went wrong. Please try again.";
      toast.error(msg);
    } finally {
      setLoading(false);
    }
  };

  return (
    <section id="booking" className="relative z-10 py-28 md:py-40 bg-[#0B132B]/40" data-testid="booking">
      <div className="max-w-[1400px] mx-auto px-6 lg:px-12 grid grid-cols-1 lg:grid-cols-12 gap-12">
        <Reveal className="lg:col-span-5">
          <p className="text-sm font-500 tracking-[0.3em] uppercase text-[#D95A41] mb-6">Consultation</p>
          <h2 className="text-4xl md:text-6xl font-900 tracking-tightest leading-[1] text-[#F2EFE9]">
            Book a free<br />30-minute call.
          </h2>
          <p className="mt-6 text-lg text-[#94A3B8] leading-relaxed max-w-md">
            Share a few details about what you would like to discuss and when a call would suit you.
            We will respond within one business day to confirm the time.
          </p>
          <div className="mt-8 flex items-center gap-3 text-[#94A3B8]">
            <Clock size={18} className="text-[#D95A41]" /> 30 minutes · Online or phone
          </div>
        </Reveal>

        <Reveal className="lg:col-span-7" delay={0.1}>
          {done ? (
            <div className="rounded-3xl border border-[#D95A41]/40 bg-[#050A14] p-12 text-center" data-testid="booking-success">
              <div className="w-16 h-16 rounded-full bg-[#D95A41] flex items-center justify-center mx-auto">
                <Check size={32} className="text-[#F2EFE9]" />
              </div>
              <h3 className="mt-6 text-2xl font-700 text-[#F2EFE9]">Request received.</h3>
              <p className="mt-3 text-[#94A3B8]">
                Thank you, {form.name.split(" ")[0]}. We will be in touch on{" "}
                <span className="text-[#F2EFE9]">{form.email}</span> within one business day to confirm your consultation.
              </p>
              <button
                onClick={() => {
                  setDone(false);
                  setForm({ name: "", email: "", company: "", preferred: "", notes: "" });
                }}
                className="mt-8 px-6 py-3 rounded-full border border-white/20 text-[#F2EFE9] font-500 hover:bg-white/5 transition-colors"
                data-testid="booking-again-btn"
              >
                Send another request
              </button>
            </div>
          ) : (
            <form onSubmit={submit} className="rounded-3xl border border-white/10 bg-[#050A14] p-8 md:p-10" data-testid="booking-form">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <input
                  required
                  placeholder="Full name"
                  data-testid="booking-name"
                  value={form.name}
                  onChange={(e) => setForm({ ...form, name: e.target.value })}
                  className="bg-[#0B132B] border border-white/10 rounded-xl px-4 py-3 text-[#F2EFE9] placeholder:text-[#94A3B8] focus:border-[#D95A41] outline-none transition-colors"
                />
                <input
                  required
                  type="email"
                  placeholder="Email"
                  data-testid="booking-email"
                  value={form.email}
                  onChange={(e) => setForm({ ...form, email: e.target.value })}
                  className="bg-[#0B132B] border border-white/10 rounded-xl px-4 py-3 text-[#F2EFE9] placeholder:text-[#94A3B8] focus:border-[#D95A41] outline-none transition-colors"
                />
                <input
                  placeholder="Company (optional)"
                  data-testid="booking-company"
                  value={form.company}
                  onChange={(e) => setForm({ ...form, company: e.target.value })}
                  className="md:col-span-2 bg-[#0B132B] border border-white/10 rounded-xl px-4 py-3 text-[#F2EFE9] placeholder:text-[#94A3B8] focus:border-[#D95A41] outline-none transition-colors"
                />
                <input
                  placeholder="Preferred day / time (optional) — e.g., Tue afternoon"
                  data-testid="booking-preferred"
                  value={form.preferred}
                  onChange={(e) => setForm({ ...form, preferred: e.target.value })}
                  className="md:col-span-2 bg-[#0B132B] border border-white/10 rounded-xl px-4 py-3 text-[#F2EFE9] placeholder:text-[#94A3B8] focus:border-[#D95A41] outline-none transition-colors"
                />
                <textarea
                  placeholder="What would you like to discuss? (optional)"
                  rows={4}
                  data-testid="booking-notes"
                  value={form.notes}
                  onChange={(e) => setForm({ ...form, notes: e.target.value })}
                  className="md:col-span-2 bg-[#0B132B] border border-white/10 rounded-xl px-4 py-3 text-[#F2EFE9] placeholder:text-[#94A3B8] focus:border-[#D95A41] outline-none transition-colors resize-none"
                />
              </div>

              <button
                type="submit"
                disabled={loading}
                data-testid="booking-submit"
                className="mt-6 w-full py-4 rounded-full bg-[#D95A41] text-[#F2EFE9] font-700 hover:bg-[#E6735A] transition-colors disabled:opacity-60 flex items-center justify-center gap-2"
              >
                {loading ? "Sending…" : <>Request a consultation <ArrowRight size={16} /></>}
              </button>
              <p className="mt-3 text-center text-xs text-[#94A3B8]">
                No obligation — a short conversation about how we can help.
              </p>
            </form>
          )}
        </Reveal>
      </div>
    </section>
  );
}
